/**
 * Admin API Service
 *
 * Handles all API communication with the admin backend.
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';

// Create axios instance with defaults
const api = axios.create({
  baseURL: `${API_URL}/admin/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Request Deduplication - Prevents duplicate in-flight GET requests
// React StrictMode and component re-renders can cause duplicate API calls.
// This deduplicates them at the axios level.
// ============================================================================
const pendingRequests = new Map();
const CACHE_TTL_MS = 5000; // 5 second cache for rapid navigation
const responseCache = new Map();

function getRequestKey(config) {
  // Create unique key from method + url + params
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${config.method}:${config.url}:${params}`;
}

// Wrapper for GET requests with deduplication
const originalGet = api.get.bind(api);
api.get = async function dedupGet(url, config = {}) {
  const fullConfig = { ...config, url, method: 'get' };
  const key = getRequestKey(fullConfig);

  // Check response cache first (for rapid back/forth navigation)
  const cached = responseCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    console.log('[axios] Cache HIT:', url);
    return cached.response;
  }

  // Check for pending request (deduplication)
  if (pendingRequests.has(key)) {
    console.log('[axios] Dedup - reusing pending request:', url);
    return pendingRequests.get(key);
  }

  // Make the request
  const promise = originalGet(url, config)
    .then(response => {
      // Cache the response
      responseCache.set(key, { response, timestamp: Date.now() });
      return response;
    })
    .finally(() => {
      pendingRequests.delete(key);
    });

  pendingRequests.set(key, promise);
  return promise;
};

// ============================================================================
// Mutation Cache Invalidation
// When data changes (POST/PUT/DELETE/PATCH), clear related cache entries
// ============================================================================
function invalidateCacheForUrl(url) {
  // Extract the base resource from the URL (e.g., /employees/123 -> /employees)
  const basePath = url.split('/').slice(0, 2).join('/');

  // Clear all cache entries that match this base path
  let cleared = 0;
  for (const [key] of responseCache) {
    if (key.includes(basePath)) {
      responseCache.delete(key);
      cleared++;
    }
  }
  if (cleared > 0) {
    console.log(`[axios] Cache invalidated: ${cleared} entries for ${basePath}`);
  }
}

// Wrap mutation methods to invalidate cache on success
const originalPost = api.post.bind(api);
api.post = async function mutatingPost(url, data, config) {
  const response = await originalPost(url, data, config);
  // Don't invalidate on auth endpoints (login/logout don't change list data)
  if (!url.startsWith('/auth/')) {
    invalidateCacheForUrl(url);
  }
  return response;
};

const originalPut = api.put.bind(api);
api.put = async function mutatingPut(url, data, config) {
  const response = await originalPut(url, data, config);
  invalidateCacheForUrl(url);
  return response;
};

const originalPatch = api.patch.bind(api);
api.patch = async function mutatingPatch(url, data, config) {
  const response = await originalPatch(url, data, config);
  invalidateCacheForUrl(url);
  return response;
};

const originalDelete = api.delete.bind(api);
api.delete = async function mutatingDelete(url, config) {
  const response = await originalDelete(url, config);
  invalidateCacheForUrl(url);
  return response;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    console.log('[axios] Request interceptor:', config.method, config.url);
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('[axios] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log('[axios] Response interceptor success:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('[axios] Response interceptor error:', error);
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Clear cache on logout
export function clearApiCache() {
  pendingRequests.clear();
  responseCache.clear();
  console.log('[axios] API cache cleared');
}

// ============================================================================
// Auth API
// ============================================================================

export const authApi = {
  async login(email, password) {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  async backupLogin(username, password) {
    const response = await api.post('/auth/backup-login', { username, password });
    return response.data;
  },

  async signup(data) {
    const response = await api.post('/auth/signup', data);
    return response.data;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      // Note: Don't clear API cache - it's global/shared, not user-specific
    }
  },

  async getMe() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async validateInvitation(token) {
    const response = await api.get('/auth/validate-invitation', { params: { token } });
    return response.data;
  },

  async completeOnboarding(token, password) {
    const response = await api.post('/auth/complete-onboarding', { token, password });
    return response.data;
  },

  async requestPasswordReset(email) {
    const response = await api.post('/auth/password-reset/request', { email });
    return response.data;
  },
};

// ============================================================================
// Employee API
// ============================================================================

export const employeeApi = {
  async listAll(params = {}) {
    const response = await api.get('/employees', { params });
    return response.data;
  },

  async listPending() {
    const response = await api.get('/employees/pending');
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/employees/${id}`);
    return response.data;
  },

  async approve(id, role) {
    const response = await api.post(`/employees/${id}/approve`, { role });
    return response.data;
  },

  async reject(id, reason) {
    const response = await api.post(`/employees/${id}/reject`, { reason });
    return response.data;
  },

  async suspend(id, reason) {
    const response = await api.post(`/employees/${id}/suspend`, { reason });
    return response.data;
  },

  async updateRole(id, role) {
    const response = await api.patch(`/employees/${id}/role`, { role });
    return response.data;
  },

  async onboard(data) {
    const response = await api.post('/employees/onboard', data);
    return response.data;
  },

  async resendInvite(id) {
    const response = await api.post(`/employees/${id}/resend-invite`);
    return response.data;
  },
};

// ============================================================================
// Config API
// ============================================================================

export const configApi = {
  async listCategories() {
    const response = await api.get('/config/categories');
    return response.data;
  },

  async listVariables(category = null) {
    const params = category ? { category } : {};
    const response = await api.get('/config/variables', { params });
    return response.data;
  },

  async getVariable(key, reveal = false) {
    const response = await api.get(`/config/variables/${key}`, {
      params: { reveal },
    });
    return response.data;
  },

  async createVariable(data) {
    const response = await api.post('/config/variables', data);
    return response.data;
  },

  async updateVariable(key, value, description = null) {
    // Backend expects { value: "...", description: "..." } object
    const data = { value };
    if (description !== null && description !== undefined) {
      data.description = description;
    }
    const response = await api.put(`/config/variables/${key}`, data);
    return response.data;
  },

  async deleteVariable(key) {
    const response = await api.delete(`/config/variables/${key}`);
    return response.data;
  },
};

// ============================================================================
// Users API (App Users)
// ============================================================================

export const usersApi = {
  async list(params = {}) {
    const response = await api.get('/users', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  async suspend(id, reason) {
    const response = await api.post(`/users/${id}/suspend`, { reason });
    return response.data;
  },

  async unsuspend(id) {
    const response = await api.post(`/users/${id}/unsuspend`);
    return response.data;
  },

  async adjustCredits(id, amount, reason) {
    const response = await api.post(`/users/${id}/credits`, { amount, reason });
    return response.data;
  },

  async getCreditDetails(id) {
    const response = await api.get(`/users/${id}/credits/details`);
    return response.data;
  },

  async reconcileCredits(id, reason) {
    const response = await api.post(`/users/${id}/credits/reconcile`, { reason });
    return response.data;
  },
};

// ============================================================================
// Audit API
// ============================================================================

export const auditApi = {
  async list(params = {}) {
    const response = await api.get('/audit/logs', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/audit/logs/${id}`);
    return response.data;
  },

  async export(params = {}) {
    const response = await api.get('/audit/export', { params });
    return response.data;
  },
};

// ============================================================================
// Health API (moved below - see "Cloud-first health monitoring")
// ============================================================================

// ============================================================================
// Organizations API
// ============================================================================

export const organizationsApi = {
  async list(params = {}) {
    const response = await api.get('/organizations', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/organizations/${id}`);
    return response.data;
  },

  async getMembers(id, params = {}) {
    const response = await api.get(`/organizations/${id}/members`, { params });
    return response.data;
  },

  async suspend(id, reason) {
    const response = await api.post(`/organizations/${id}/suspend`, { reason });
    return response.data;
  },

  async unsuspend(id) {
    const response = await api.post(`/organizations/${id}/unsuspend`);
    return response.data;
  },

  async updatePlan(id, plan) {
    const response = await api.patch(`/organizations/${id}/plan`, { plan });
    return response.data;
  },

  async getStats() {
    const response = await api.get('/organizations/stats/overview');
    return response.data;
  },

  async getTiles(params = {}) {
    console.log("[adminApi] getTiles called with params:", params);
    try {
      const response = await api.get('/organizations/tiles', { params });
      console.log("[adminApi] getTiles response:", response.status, response.data);
      return response.data;
    } catch (err) {
      console.error("[adminApi] getTiles error:", err);
      throw err;
    }
  },

  async getHeatmap() {
    const response = await api.get('/organizations/heatmap');
    return response.data;
  },
};

// ============================================================================
// Monitoring API
// ============================================================================

/**
 * Monitoring API
 *
 * Cloud-first architecture (ADR-078).
 * Local Prometheus/Grafana removed - all observability via Grafana Cloud.
 */
export const monitoringApi = {
  async listDashboards() {
    const response = await api.get('/monitoring/dashboards');
    return response.data;
  },

  async getSystemHealth() {
    const response = await api.get('/monitoring/health');
    return response.data;
  },

  async getAdminSummary() {
    const response = await api.get('/monitoring/admin-summary');
    return response.data;
  },

  async getCloudStatus() {
    const response = await api.get('/monitoring/cloud-status');
    return response.data;
  },
};

// ============================================================================
// Health API
// ============================================================================

/**
 * Health API
 *
 * Cloud-first health monitoring (ADR-078).
 * Provides cloud service health checks.
 */
export const healthApi = {
  async getHealth() {
    // Note: Uses /admin/health (not /admin/api/health)
    const baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
    const response = await fetch(`${baseUrl}/admin/health`);
    return response.json();
  },

  async getCloudServicesHealth() {
    // Note: Uses /admin/health (not /admin/api/health) - see router registration
    const baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
    const token = localStorage.getItem('admin_token');
    const response = await fetch(`${baseUrl}/admin/health/cloud-services`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  async getCoreServicesHealth() {
    // Note: Uses /admin/health (not /admin/api/health) - see router registration
    const baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
    const token = localStorage.getItem('admin_token');
    const response = await fetch(`${baseUrl}/admin/health/core`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  async getObservabilityServicesHealth() {
    // Note: Uses /admin/health (not /admin/api/health) - see router registration
    const baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
    const token = localStorage.getItem('admin_token');
    const response = await fetch(`${baseUrl}/admin/health/observability`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  },

  async getReadiness() {
    const baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
    const response = await fetch(`${baseUrl}/admin/ready`);
    return response.json();
  },

  async getLiveness() {
    const baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
    const response = await fetch(`${baseUrl}/admin/live`);
    return response.json();
  },
};

// ============================================================================
// Logs API
// ============================================================================

export const logsApi = {
  async getRecent(params = {}) {
    const response = await api.get('/logs/recent', { params });
    return response.data;
  },

  async getBackendLogs(params = {}) {
    const response = await api.get('/logs/backend', { params });
    return response.data;
  },

  async createTestLog(level, message) {
    const response = await api.post('/logs/test', null, {
      params: { level, message },
    });
    return response.data;
  },

  // SSE stream URL builder
  getStreamUrl(level = null, module = null) {
    const token = localStorage.getItem('admin_token');
    const baseUrl = import.meta.env.VITE_ADMIN_API_URL || 'http://localhost:4000';
    let url = `${baseUrl}/admin/api/logs/stream`;

    const params = new URLSearchParams();
    if (level) params.append('level', level);
    if (module) params.append('module', module);
    // Add token as query param for SSE (headers not supported)
    if (token) params.append('token', token);

    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  },
};

// ============================================================================
// Alerts API
// ============================================================================

/**
 * Alerts API
 *
 * Cloud-first architecture (ADR-078).
 * Local AlertManager removed - silences and alerting via Grafana Cloud.
 */
export const alertsApi = {
  async list(params = {}) {
    const response = await api.get('/alerts', { params });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/alerts/${id}`);
    return response.data;
  },

  async acknowledge(id, notes = null) {
    const response = await api.post(`/alerts/${id}/acknowledge`, { notes });
    return response.data;
  },

  async resolve(id, data) {
    const response = await api.post(`/alerts/${id}/resolve`, data);
    return response.data;
  },

  async escalate(id, data) {
    const response = await api.post(`/alerts/${id}/escalate`, data);
    return response.data;
  },

  async getStats(period = '24h') {
    const response = await api.get('/alerts/stats', { params: { period } });
    return response.data;
  },

  async getHistory(id, limit = 50) {
    const response = await api.get(`/alerts/${id}/history`, { params: { limit } });
    return response.data;
  },

  // Silences - Now managed in Grafana Cloud
  // Returns link to Grafana Cloud Alerting silences page
  async getSilences() {
    const response = await api.get('/alerts/silences/active');
    return response.data;
  },

  // Runbooks
  async listRunbooks(search = null) {
    const params = search ? { search } : {};
    const response = await api.get('/alerts/runbooks', { params });
    return response.data;
  },

  async getRunbook(id) {
    const response = await api.get(`/alerts/runbooks/${id}`);
    return response.data;
  },

  async getRunbookForAlert(alertName) {
    const response = await api.get(`/alerts/runbooks/for-alert/${alertName}`);
    return response.data;
  },

  // Escalation contacts
  async getEscalationContacts(level = null, category = null) {
    const params = {};
    if (level) params.level = level;
    if (category) params.category = category;
    const response = await api.get('/alerts/escalation-contacts', { params });
    return response.data;
  },

  // Cloud Alerting Status (ADR-078)
  async getAlertingStatus() {
    const response = await api.get('/alerts/alerting/status');
    return response.data;
  },

  // Legacy AlertManager status - deprecated, redirects to cloud
  async getAlertManagerStatus() {
    const response = await api.get('/alerts/alertmanager/status');
    return response.data;
  },
};

// ============================================================================
// Knowledge Base API
// ============================================================================

// ============================================================================
// Finance API
// ============================================================================

export const financeApi = {
  async getDeferredRevenue(creditValueUsd = 1.0) {
    const response = await api.get('/finance/deferred-revenue', {
      params: { credit_value_usd: creditValueUsd },
    });
    return response.data;
  },

  async getCreditTransactionSummary(period = '30d') {
    const response = await api.get('/finance/credit-transactions/summary', {
      params: { period },
    });
    return response.data;
  },

  async getCreditCycleJobHistory(limit = 10) {
    const response = await api.get('/finance/cron-jobs/credit-cycles', {
      params: { limit },
    });
    return response.data;
  },
};

// ============================================================================
// Economics API (ADR-079: Credit Economics Revenue Recognition)
// ============================================================================

export const economicsApi = {
  /**
   * Get credit values for all tiers
   */
  async getTierCreditValues() {
    const response = await api.get('/economics/tier-values');
    return response.data;
  },

  /**
   * Get credit economics aggregation
   * @param {Object} params - Query parameters
   * @param {string} [params.organizationId] - Filter by organization
   * @param {string} [params.month] - Month filter (YYYY-MM format)
   */
  async getCreditEconomics(params = {}) {
    const response = await api.get('/economics/credit-economics', {
      params: {
        organization_id: params.organizationId,
        month: params.month,
      },
    });
    return response.data;
  },

  /**
   * Get gross margin per credit
   * @param {string} period - Time period (7d, 30d, 90d)
   */
  async getMarginPerCredit(period = '30d') {
    const response = await api.get('/economics/margin-per-credit', {
      params: { period },
    });
    return response.data;
  },

  /**
   * Get profitability breakdown by tier
   * @param {string} period - Time period (7d, 30d, 90d)
   */
  async getProfitabilityByTier(period = '30d') {
    const response = await api.get('/economics/profitability-by-tier', {
      params: { period },
    });
    return response.data;
  },

  /**
   * Get monthly revenue recognition summary
   * @param {number} months - Number of months to retrieve (1-24)
   */
  async getMonthlyRevenueRecognition(months = 6) {
    const response = await api.get('/economics/revenue-recognition/monthly', {
      params: { months },
    });
    return response.data;
  },

  /**
   * Manually refresh the credit economics materialized view
   */
  async refreshMaterializedView() {
    const response = await api.post('/economics/refresh-materialized-view');
    return response.data;
  },

  /**
   * Get cost-to-serve metrics (Phase 5 Unit Economics)
   * @param {string} period - Time period (7d, 30d, 90d)
   */
  async getCostToServe(period = '30d') {
    const response = await api.get('/economics/cost-to-serve', {
      params: { period },
    });
    return response.data;
  },

  /**
   * Get monthly P&L summary (Phase 5 Unit Economics)
   * @param {number} months - Number of months to retrieve (1-24)
   */
  async getMonthlyPnL(months = 6) {
    const response = await api.get('/economics/monthly-pnl', {
      params: { months },
    });
    return response.data;
  },

  /**
   * Get complete unit economics summary (Phase 5)
   * @param {string} period - Time period (7d, 30d, 90d)
   */
  async getUnitEconomicsSummary(period = '30d') {
    const response = await api.get('/economics/unit-economics-summary', {
      params: { period },
    });
    return response.data;
  },

  /**
   * Refresh all economics materialized views
   */
  async refreshAllViews() {
    const response = await api.post('/economics/refresh-all-views');
    return response.data;
  },

  /**
   * F4 — Get the canonical $ COGS / credit headline + segment cuts.
   * Reconciles against SUM(usage_logs.llm_cost_usd) / SUM(usage_logs.credits_used)
   * for the same window. Coverage % surfaces F1b metering completeness.
   * @param {string} window - "7d" or "30d"
   */
  async getCogsPerCredit(window = '30d') {
    const response = await api.get('/economics/cogs-per-credit', {
      params: { window },
    });
    return response.data;
  },
};

// ============================================================================
// Analytics API (ADR-081: PM Analytics Dashboard)
// ============================================================================

export const analyticsApi = {
  /**
   * Get Monthly Recurring Revenue metrics
   */
  async getMRR() {
    const response = await api.get('/analytics/mrr');
    return response.data;
  },

  /**
   * Get Annual Recurring Revenue metrics
   */
  async getARR() {
    const response = await api.get('/analytics/arr');
    return response.data;
  },

  /**
   * Get MRR growth trend over time
   * @param {number} months - Number of months to retrieve
   */
  async getMRRGrowth(months = 6) {
    const response = await api.get('/analytics/mrr-growth', {
      params: { months },
    });
    return response.data;
  },

  /**
   * Get revenue breakdown by tier
   */
  async getRevenueByTier() {
    const response = await api.get('/analytics/revenue-by-tier');
    return response.data;
  },

  /**
   * Get churn analytics
   */
  async getChurn() {
    const response = await api.get('/analytics/churn');
    return response.data;
  },

  /**
   * Get at-risk users analysis
   * @param {number} riskThresholdDays - Days of inactivity threshold
   * @param {number} limit - Max results
   */
  async getAtRiskUsers(riskThresholdDays = 14, limit = 20) {
    const response = await api.get('/analytics/at-risk', {
      params: {
        risk_threshold_days: riskThresholdDays,
        limit,
      },
    });
    return response.data;
  },

  /**
   * Get retention cohort analysis
   * @param {number} months - Number of months of cohorts
   */
  async getCohorts(months = 6) {
    const response = await api.get('/analytics/cohorts', {
      params: { months },
    });
    return response.data;
  },

  /**
   * Get feature usage metrics
   * @param {string} period - Time period (7d, 30d, 90d)
   */
  async getFeatureUsage(period = '30d') {
    const response = await api.get('/analytics/feature-usage', {
      params: { period },
    });
    return response.data;
  },

  /**
   * Get power users analysis
   * @param {number} limit - Max results
   * @param {string} period - Time period
   */
  async getPowerUsers(limit = 20, period = '30d') {
    const response = await api.get('/analytics/power-users', {
      params: { limit, period },
    });
    return response.data;
  },

  /**
   * Get analytics summary for dashboard
   */
  async getSummary() {
    const response = await api.get('/analytics/summary');
    return response.data;
  },
};

// ============================================================================
// Waitlist API (app-backend)
// ============================================================================

// Waitlist endpoints are on app-backend (port 8000), not admin-backend
const APP_API_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:8000';

const appApi = axios.create({
  baseURL: `${APP_API_URL}/api/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor for app-backend calls
appApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors
appApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const waitlistApi = {
  /**
   * Get ranked waitlist entries with filtering
   * @param {Object} params - Query parameters
   * @param {number} [params.limit] - Max entries to return (default 50)
   * @param {number} [params.offset] - Pagination offset
   * @param {string} [params.entry_status] - Filter by status (pending, invited, registered, all)
   * @param {number} [params.min_score] - Minimum ranking score
   * @param {string} [params.organization] - Filter by organization (partial match)
   * @param {string} [params.sap_experience] - Filter by SAP experience (0-2, 2-5, 5-10, 10+)
   * @param {string} [params.role] - Filter by role (partial match)
   */
  async getRanked(params = {}) {
    const response = await appApi.get('/admin/waitlist/ranked', { params });
    return response.data;
  },

  /**
   * Get waitlist statistics
   */
  async getStats() {
    const response = await appApi.get('/admin/waitlist/stats');
    return response.data;
  },

  /**
   * Get score distribution histogram
   */
  async getScoreDistribution() {
    const response = await appApi.get('/admin/waitlist/score-distribution');
    return response.data;
  },

  /**
   * Get organization groups
   * @param {Object} params - Query parameters
   * @param {number} [params.limit] - Max organizations to return
   * @param {number} [params.offset] - Pagination offset
   * @param {boolean} [params.enterprise_only] - Only show enterprise orgs
   */
  async getOrganizations(params = {}) {
    const response = await appApi.get('/admin/waitlist/organizations', { params });
    return response.data;
  },

  /**
   * Get members of a specific organization
   * @param {string} orgNormalized - Normalized organization name
   */
  async getOrganizationMembers(orgNormalized) {
    const response = await appApi.get(`/admin/waitlist/organizations/${encodeURIComponent(orgNormalized)}/members`);
    return response.data;
  },

  /**
   * Invite an individual waitlist entry
   * @param {string} entryId - Waitlist entry UUID
   * @param {boolean} [sendEmail=true] - Whether to send invite email
   */
  async inviteIndividual(entryId, sendEmail = true) {
    const response = await appApi.post(`/admin/waitlist/invite/${entryId}`, { send_email: sendEmail });
    return response.data;
  },

  /**
   * Invite top N users from a specific organization
   * @param {string} orgNormalized - Normalized organization name
   * @param {number} [topN=5] - Number of top users to invite
   * @param {number} [minScore] - Optional minimum score filter
   * @param {boolean} [sendEmail=true] - Whether to send invite emails
   */
  async inviteOrganizationTop(orgNormalized, topN = 5, minScore = null, sendEmail = true) {
    const response = await appApi.post(`/admin/waitlist/organizations/${encodeURIComponent(orgNormalized)}/invite`, {
      top_n: topN,
      min_score: minScore,
      send_email: sendEmail,
    });
    return response.data;
  },

  /**
   * Create and process a batch invite job
   * @param {number} [batchSize=10] - Number of invites to send
   * @param {number} [minScore] - Optional minimum score filter
   */
  async createBatch(batchSize = 10, minScore = null) {
    const response = await appApi.post('/admin/waitlist/batch', {
      batch_size: batchSize,
      min_score: minScore,
    });
    return response.data;
  },

  /**
   * List batch invite history
   * @param {Object} params - Query parameters
   * @param {number} [params.limit] - Max batches to return
   * @param {number} [params.offset] - Pagination offset
   * @param {string} [params.status] - Filter by status
   */
  async listBatches(params = {}) {
    const response = await appApi.get('/admin/waitlist/batches', { params });
    return response.data;
  },

  /**
   * Get batch statistics
   */
  async getBatchStats() {
    const response = await appApi.get('/admin/waitlist/batches/stats');
    return response.data;
  },

  /**
   * Get status of a specific batch
   * @param {string} batchId - Batch job UUID
   */
  async getBatchStatus(batchId) {
    const response = await appApi.get(`/admin/waitlist/batches/${batchId}`);
    return response.data;
  },

  /**
   * Recalculate all ranking scores
   * Warning: Can be slow for large waitlists
   */
  async recalculateScores() {
    const response = await appApi.post('/admin/waitlist/recalculate-scores');
    return response.data;
  },
};

// ============================================================================
// Invites API
// ============================================================================

export const invitesApi = {
  async list(params = {}) {
    // Note: Trailing slash required to avoid 307 redirect
    const response = await api.get('/invites/', { params });
    return response.data;
  },

  async generate(count = 1, sourceType = 'admin', validityDays = null, assignedEmail = null) {
    const response = await api.post('/invites/generate', {
      count,
      source_type: sourceType,
      validity_days: validityDays,
      assigned_email: assignedEmail,
    });
    return response.data;
  },

  async getAnalytics(params = {}) {
    const response = await api.get('/invites/analytics', { params });
    return response.data;
  },

  async getRankings(limit = 50, minConversions = 1) {
    const response = await api.get('/invites/rankings', {
      params: { limit, min_conversions: minConversions },
    });
    return response.data;
  },

  async getByCode(code) {
    const response = await api.get(`/invites/${code}`);
    return response.data;
  },

  async revoke(code, reason) {
    const response = await api.delete(`/invites/${code}`, {
      data: { reason },
    });
    return response.data;
  },

  async refreshRankings() {
    const response = await api.post('/invites/refresh-rankings');
    return response.data;
  },
};

// ============================================================================
// Feedback API
// ============================================================================

export const feedbackApi = {
  async list(params = {}) {
    const response = await api.get('/feedback', { params });
    return response.data;
  },

  async getStats(period = '30d') {
    const response = await api.get('/feedback/stats', { params: { period } });
    return response.data;
  },

  async getSummaries(period = 'week', limit = 10) {
    const response = await api.get('/feedback/summaries', {
      params: { period, limit },
    });
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/feedback/${id}`);
    return response.data;
  },

  async update(id, data) {
    const response = await api.patch(`/feedback/${id}`, data);
    return response.data;
  },

  async triggerClassification(limit = 100) {
    const response = await api.post('/feedback/classify', { limit });
    return response.data;
  },

  async generateSummary(period = 'weekly') {
    const response = await api.post('/feedback/generate-summary', { period });
    return response.data;
  },
};

// ============================================================================
// Knowledge Base API
// ============================================================================

// ============================================================================
// Tiers API (ADR-045: Read-only from Stripe)
// ============================================================================

export const tiersApi = {
  async list() {
    const response = await api.get('/tiers');
    return response.data;
  },

  async refresh() {
    const response = await api.post('/tiers/refresh');
    return response.data;
  },

  async getSyncStatus() {
    const response = await api.get('/tiers/sync-status');
    return response.data;
  },

  async syncToDatabase() {
    const response = await api.post('/tiers/sync');
    return response.data;
  },
};

// ============================================================================
// Stripe Admin API (ADR-082: Stripe Admin Management)
// ============================================================================

export const stripeApi = {
  /**
   * Get Stripe webhook health status
   * Returns status, last webhook time, event counts, and configuration
   */
  async getWebhookHealth() {
    const response = await api.get('/stripe/webhook-health');
    return response.data;
  },

  /**
   * Reconcile all subscriptions from Stripe
   * Heavy operation - syncs all Stripe subscriptions with database
   */
  async reconcileAll() {
    const response = await api.post('/stripe/reconcile');
    return response.data;
  },

  /**
   * Reconcile a single customer's subscriptions
   * @param {string} email - Customer email address
   */
  async reconcileCustomer(email) {
    const response = await api.post(`/stripe/reconcile/customer?email=${encodeURIComponent(email)}`);
    return response.data;
  },

  /**
   * Detect subscription drift between Stripe and database
   * Returns list of subscriptions with mismatched data
   */
  async getSubscriptionDrift() {
    const response = await api.get('/stripe/subscriptions/drift');
    return response.data;
  },

  /**
   * Fix drift for a single subscription
   * @param {string} subscriptionId - Stripe subscription ID
   */
  async fixDrift(subscriptionId) {
    const response = await api.post(`/stripe/fix-drift/${encodeURIComponent(subscriptionId)}`);
    return response.data;
  },

  /**
   * Comprehensive drift preview (read-only, never writes).
   * Detects all 15 drift classes between Stripe and DB.
   * @param {object} opts
   * @param {number} opts.envKey - 100 (dev), 200 (staging), 300 (prd)
   * @param {string[]} opts.driftClasses - optional filter
   * @see ADR-186
   */
  async getDriftPreview({ envKey, driftClasses } = {}) {
    const params = new URLSearchParams();
    if (envKey) params.set('env_key', String(envKey));
    if (driftClasses && driftClasses.length) {
      params.set('drift_classes', driftClasses.join(','));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/stripe/sync/preview${qs}`);
    return response.data;
  },

  /**
   * Comprehensive drift sync (apply auto-fixable fixes).
   * Runs preview internally then applies. Idempotent via Stripe idempotency_keys.
   * @param {object} body
   * @param {number} body.envKey
   * @param {string[]} body.driftClasses - optional filter
   * @see ADR-186
   */
  async runDriftSync({ envKey, driftClasses } = {}) {
    const payload = {};
    if (envKey) payload.env_key = envKey;
    if (driftClasses && driftClasses.length) payload.drift_classes = driftClasses;
    const response = await api.post('/stripe/sync/run', payload);
    return response.data;
  },

  /**
   * Sync subscription_tiers table from Stripe (env-aware).
   * Migration 159 adds env_key column; this admin call refreshes the
   * rows for the current backend instance's env_key from its configured
   * Stripe key (test mode for dev/staging, live mode for prd).
   * @see ADR-186 follow-up
   */
  async syncTiersFromStripe() {
    const response = await api.post('/tiers/sync');
    return response.data;
  },
};

// ============================================================================
// Knowledge Base API
// ============================================================================

export const kbApi = {
  async getStats() {
    const response = await api.get('/kb/stats');
    return response.data;
  },

  async getSources(params = {}) {
    const response = await api.get('/kb/sources', { params });
    return response.data;
  },

  async getTopics() {
    const response = await api.get('/kb/topics');
    return response.data;
  },

  async getGroupedStats(groupBy = 'domain') {
    const response = await api.get('/kb/sources/grouped', { params: { group_by: groupBy } });
    return response.data;
  },

  async categorizeSource(sourceId, data) {
    const response = await api.patch(`/kb/sources/${sourceId}/categorize`, data);
    return response.data;
  },

  async bulkCategorize(sourceIds, category, tags = null, addTags = null) {
    const response = await api.post('/kb/sources/bulk-categorize', {
      source_ids: sourceIds,
      category,
      tags,
      add_tags: addTags,
    });
    return response.data;
  },

  async uploadPDF(file, title = null, description = null) {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);

    const response = await api.post('/kb/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async ingestURL(url, title = null, description = null) {
    const response = await api.post('/kb/ingest-url', { url, title, description });
    return response.data;
  },

  async deleteSource(sourceId) {
    const response = await api.delete(`/kb/sources/${sourceId}`);
    return response.data;
  },
};

// ============================================================================
// Support Tools API (Phase 6)
// ============================================================================

export const supportApi = {
  /**
   * Enhanced user lookup with multi-field search
   * @param {string} query - Search query (email, name, org, user ID)
   * @param {number} limit - Max results
   */
  async userLookup(query, limit = 20) {
    const response = await api.get('/support/user-lookup', {
      params: { query, limit },
    });
    return response.data;
  },

  /**
   * Get full customer context for support panel
   * @param {string} userId - User ID
   */
  async getCustomerContext(userId) {
    const response = await api.get(`/support/customer-context/${userId}`);
    return response.data;
  },

  /**
   * Get user activity summary for timeline
   * @param {string} userId - User ID
   */
  async getUserActivity(userId) {
    const response = await api.get(`/support/user-activity/${userId}`);
    return response.data;
  },

  /**
   * Get credit usage graph data
   * @param {string} userId - User ID
   * @param {string} period - Time period (7d, 30d, 90d, 1y)
   */
  async getCreditUsageGraph(userId, period = '30d') {
    const response = await api.get(`/support/credit-usage-graph/${userId}`, {
      params: { period },
    });
    return response.data;
  },

  /**
   * Bulk credit adjustment
   * @param {Array} adjustments - Array of {user_id, amount, reason}
   */
  async bulkCreditAdjustment(adjustments) {
    const response = await api.post('/support/bulk-credit-adjustment', {
      adjustments,
    });
    return response.data;
  },

  /**
   * Bulk status change (suspend/unsuspend)
   * @param {Array} changes - Array of {user_id, action, reason}
   */
  async bulkStatusChange(changes) {
    const response = await api.post('/support/bulk-status-change', {
      changes,
    });
    return response.data;
  },

  /**
   * Add support note for a user
   * @param {string} userId - User ID
   * @param {string} note - Note text
   */
  async addSupportNote(userId, note) {
    const response = await api.post(`/support/notes/${userId}`, { note });
    return response.data;
  },

  /**
   * Get support notes for a user
   * @param {string} userId - User ID
   */
  async getSupportNotes(userId) {
    const response = await api.get(`/support/notes/${userId}`);
    return response.data;
  },
};

// ============================================================================
// Email API (ADR-080: Email Service Architecture)
// ============================================================================

export const emailApi = {
  // Template Management
  /**
   * Get all email templates
   */
  async getTemplates() {
    const response = await api.get('/email/templates');
    return response.data;
  },

  /**
   * Get a specific template by key
   * @param {string} templateKey - Template key
   */
  async getTemplate(templateKey) {
    const response = await api.get(`/email/templates/${templateKey}`);
    return response.data;
  },

  /**
   * Update an email template
   * @param {string} templateKey - Template key
   * @param {Object} data - Template data (subject, html_body, text_body, is_active)
   */
  async updateTemplate(templateKey, data) {
    const response = await api.put(`/email/templates/${templateKey}`, data);
    return response.data;
  },

  /**
   * Send a test email for a template
   * @param {string} templateKey - Template key
   * @param {string} recipientEmail - Email to send test to
   * @param {Object} variables - Template variables for testing
   */
  async sendTestEmail(templateKey, recipientEmail, variables = {}) {
    const response = await api.post(`/email/templates/${templateKey}/test`, {
      recipient_email: recipientEmail,
      variables,
    });
    return response.data;
  },

  // Delivery Analytics
  /**
   * Get delivery analytics summary
   * @param {number} days - Number of days to look back
   */
  async getDeliveryAnalytics(days = 30) {
    const response = await api.get('/email/analytics/delivery', {
      params: { days },
    });
    return response.data;
  },

  /**
   * Get daily delivery stats
   * @param {number} days - Number of days
   */
  async getDailyStats(days = 30) {
    const response = await api.get('/email/analytics/daily', {
      params: { days },
    });
    return response.data;
  },

  /**
   * Get per-template performance stats
   * @param {number} days - Number of days
   */
  async getTemplateStats(days = 30) {
    const response = await api.get('/email/analytics/templates', {
      params: { days },
    });
    return response.data;
  },

  // Delivery Log
  /**
   * Get delivery log entries
   * @param {Object} params - Query params (page, limit, status, template_key, recipient_email)
   */
  async getDeliveryLog(params = {}) {
    const response = await api.get('/email/delivery-log', { params });
    return response.data;
  },

  // Policy Notifications
  /**
   * Get policy notification history
   * @param {number} limit - Max results
   */
  async getPolicyNotificationHistory(limit = 20) {
    const response = await api.get('/email/policy-notifications/history', {
      params: { limit },
    });
    return response.data;
  },

  /**
   * Get latest policy notification stats
   */
  async getPolicyNotificationStats() {
    const response = await api.get('/email/policy-notifications/stats');
    return response.data;
  },

  /**
   * Trigger policy change notification
   * @param {Object} data - Policy notification data
   */
  async triggerPolicyNotification(data) {
    const response = await api.post('/email/policy-notifications/trigger', data);
    return response.data;
  },
};

// ============================================================================
// Jobs API (ADR-082: Scheduled Jobs Management)
// ============================================================================

export const jobsApi = {
  /**
   * List all scheduled jobs
   * @param {Object} params - Query parameters
   * @param {string} [params.job_type] - Filter by job type (pg_cron, celery_beat)
   * @param {boolean} [params.is_suspended] - Filter by suspension status
   * @param {string} [params.search] - Search in job name and display name
   * @param {number} [params.page] - Page number (default 1)
   * @param {number} [params.page_size] - Items per page (default 20, max 100)
   */
  async list(params = {}) {
    const response = await api.get('/jobs', { params });
    return response.data;
  },

  /**
   * Get job details with run history
   * @param {string} jobId - Job UUID
   */
  async getDetails(jobId) {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Trigger ad-hoc run of a job (super_admin only)
   * @param {string} jobId - Job UUID
   * @param {string} [reason] - Optional reason for ad-hoc run
   */
  async runNow(jobId, reason = null) {
    const response = await api.post(`/jobs/${jobId}/run`, { reason });
    return response.data;
  },

  /**
   * Suspend or resume a job (super_admin only)
   * @param {string} jobId - Job UUID
   * @param {boolean} suspend - True to suspend, false to resume
   * @param {string} [reason] - Optional reason for the action
   */
  async suspend(jobId, suspend, reason = null) {
    const response = await api.patch(`/jobs/${jobId}/suspend`, { suspend, reason });
    return response.data;
  },

  /**
   * Reschedule a job's next run (super_admin only)
   * @param {string} jobId - Job UUID
   * @param {string} nextRunAt - ISO datetime for next run
   * @param {boolean} applyPermanent - If true, changes apply to all subsequent runs
   * @param {string} [reason] - Optional reason for rescheduling
   */
  async reschedule(jobId, nextRunAt, applyPermanent = false, reason = null) {
    const response = await api.patch(`/jobs/${jobId}/reschedule`, {
      next_run_at: nextRunAt,
      apply_permanent: applyPermanent,
      reason,
    });
    return response.data;
  },

  /**
   * Clear reschedule override and revert to cron schedule (super_admin only)
   * @param {string} jobId - Job UUID
   * @param {string} [reason] - Optional reason for clearing
   */
  async clearReschedule(jobId, reason = null) {
    const response = await api.delete(`/jobs/${jobId}/reschedule`, {
      params: { reason },
    });
    return response.data;
  },

  /**
   * Bulk suspend or resume multiple jobs
   * @param {string[]} jobIds - Array of job UUIDs (max 50)
   * @param {boolean} suspend - True to suspend, false to resume
   * @param {string} [reason] - Optional reason for the action
   */
  async bulkSuspend(jobIds, suspend, reason = null) {
    const response = await api.post('/jobs/bulk/suspend', {
      job_ids: jobIds,
      suspend,
      reason,
    });
    return response.data;
  },
};

// Landing Pages Management
export const landingPagesApi = {
  async getConfig() {
    const response = await api.get('/landing-pages/config');
    return response.data;
  },

  async updateConfig(data) {
    const response = await api.put('/landing-pages/config', data);
    return response.data;
  },

  async getStats() {
    const response = await api.get('/landing-pages/stats');
    return response.data;
  },

  async getGeoStats() {
    const response = await api.get('/landing-pages/geo-stats');
    return response.data;
  },
};

// Burning Questions Management (ADR-174)
export const burningQuestionsApi = {
  async list() {
    const response = await api.get('/burning-questions');
    return response.data?.questions || [];
  },

  async create({ text, display_order = 0, is_active = true }) {
    const response = await api.post('/burning-questions', { text, display_order, is_active });
    return response.data;
  },

  async update(id, patch) {
    const response = await api.patch(`/burning-questions/${id}`, patch);
    return response.data;
  },

  async remove(id) {
    await api.delete(`/burning-questions/${id}`);
  },

  async reorder(items) {
    const response = await api.post('/burning-questions/reorder', { items });
    return response.data?.questions || [];
  },
};

export default api;
